/**
 * @file error.interceptor.ts
 * @description this file implements interceptor to handler http errors
 */
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ErrorManagerService, ErrorType, AuthService } from '@core';
import { AppConstants } from '../../app.constants';

@Injectable({
  providedIn: 'root',
})
export class ErrorInterceptor implements HttpInterceptor {
  private static readonly className = 'ErrorInterceptor';
  constructor(
    private readonly errorManager: ErrorManagerService,
    private readonly authService: AuthService
  ) { }

  /**
   * @description Interceptor to handle http errors
   * @param  HttpRequest request
   * @param  HttpHandler next
   * @returns Observable
   */

  public intercept<T>(
    request: HttpRequest<T>,
    next: HttpHandler
  ): Observable<HttpEvent<T>> {
    const errorStatusUnauthorized = 401;

    // URL substrings that should immediately rethrow
    const rethrowUrlSubstrings: string[] = [
      '/member/modification',
      '/members/addressupdate',
      '/members/term-dependents',
      '/members/coverage',
      '/AuditLogs/SaveAuditLog',
      'client/flags',
      '/api/bap/mail-order/search-patient-enrollment',
      '/api/bap/member/coverage',
      '/api/bap/member/future/coverage'
    ];

    // Status + message patterns that should immediately rethrow (exactly as in original)
    const rethrowStatusMessageMatchers: {
      status: number;
      messageSubstring: string;
    }[] = [
        { status: 405, messageSubstring: 'temp-id-card' },
        { status: 422, messageSubstring: 'claims-service/rest/getclientclaim' },
        { status: 500, messageSubstring: 'third/party/assistance' },
      ];

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Case 1: 401 → logout and rethrow
        if (error.status === errorStatusUnauthorized) {
          this.authService.logout();
          return throwError(error);
        }

        // Normalize url/message to avoid null/undefined checks all over
        const url = error?.url ?? '';
        const message = error?.message ?? '';

        // Case 2: URL-based early rethrows (same includes as original)
        for (const needle of rethrowUrlSubstrings) {
          if (url.includes(needle)) {
            return throwError(error);
          }
        }

        // Case 3: Status+message pattern early rethrows (same as original)
        for (const m of rethrowStatusMessageMatchers) {
          if (error.status === m.status && message.includes(m.messageSubstring)) {
            return throwError(error);
          }
        }

        // Case 4: Generic error handling (unchanged)
        this.errorManager.throwError({
          type: ErrorType.ERROR_GENERIC,
          message: error?.error?.Message
            ? error.error.Message
            : AppConstants.ERROR.GENERIC_ERROR_MESSAGE,
          component: ErrorInterceptor.className,
          title: '',
        });

        return throwError(error);
      })
    );
  }


}
